import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle, Copy, Building2, CreditCard, Hash, Clock, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const EFTPaymentInstructions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [bookingDetails, setBookingDetails] = useState(null);
  const [copied, setCopied] = useState({});

  useEffect(() => {
    // Get booking details from navigation state
    const booking = location.state?.booking;
    if (!booking) {
      // If no booking details, redirect to home
      toast.error('No booking found. Please create a booking first.');
      navigate('/');
      return;
    }
    setBookingDetails(booking);
  }, [location, navigate]);

  const bankingDetails = {
    accountName: 'Clear2Drive',
    bank: 'Capitec Bank',
    accountType: 'Entrepreneur',
    accountNumber: '2577291821',
    branchCode: '470010'
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied({ ...copied, [field]: true });
      toast.success(`${field} copied to clipboard`);
      setTimeout(() => {
        setCopied({ ...copied, [field]: false });
      }, 2000);
    });
  };

  if (!bookingDetails) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white via-[#FAF8F5] to-[#FFF9F0] flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500">Loading payment details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-[#FAF8F5] to-[#FFF9F0] py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
            <CheckCircle className="text-green-600" size={48} />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#0B090A] mb-3">
            Booking Confirmed!
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Your booking reference: <span className="font-bold text-[#6B0B0B]">{bookingDetails.id}</span>
          </p>
        </div>

        {/* Important Notice */}
        <Card className="mb-6 border-l-4 border-amber-500 bg-amber-50/50">
          <CardContent className="pt-6">
            <div className="flex items-start space-x-3">
              <Clock className="text-amber-600 mt-1 flex-shrink-0" size={24} />
              <div>
                <h3 className="font-semibold text-amber-900 mb-2">Important: Complete Your Payment</h3>
                <p className="text-sm text-amber-800 leading-relaxed">
                  Your booking is currently <strong>pending payment</strong>. Please complete the bank transfer using the details below. 
                  Your booking will be confirmed once we receive and verify your payment (typically within 1-2 business days).
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Instructions */}
        <Card className="cinema-shadow border-2 border-[#D4AF37]/20 mb-6">
          <CardHeader className="bg-gradient-to-r from-[#6B0B0B] to-[#8B1515] text-white rounded-t-lg">
            <CardTitle className="text-2xl flex items-center">
              <Building2 className="mr-3" size={28} />
              EFT Payment Instructions
            </CardTitle>
            <CardDescription className="text-white/90 text-base">
              Please use the banking details below to complete your payment
            </CardDescription>
          </CardHeader>
          <CardContent className="p-8">
            <div className="space-y-6">
              {/* Banking Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-600">Account Name</label>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <span className="font-semibold text-gray-900">{bankingDetails.accountName}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(bankingDetails.accountName, 'Account Name')}
                      className="ml-2"
                    >
                      <Copy size={16} className={copied['Account Name'] ? 'text-green-600' : 'text-gray-600'} />
                    </Button>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-600">Bank</label>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <span className="font-semibold text-gray-900">{bankingDetails.bank}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(bankingDetails.bank, 'Bank')}
                      className="ml-2"
                    >
                      <Copy size={16} className={copied['Bank'] ? 'text-green-600' : 'text-gray-600'} />
                    </Button>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-600">Account Number</label>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <span className="font-bold text-lg text-[#6B0B0B]">{bankingDetails.accountNumber}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(bankingDetails.accountNumber, 'Account Number')}
                      className="ml-2"
                    >
                      <Copy size={16} className={copied['Account Number'] ? 'text-green-600' : 'text-gray-600'} />
                    </Button>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-600">Branch Code</label>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <span className="font-semibold text-gray-900">{bankingDetails.branchCode}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(bankingDetails.branchCode, 'Branch Code')}
                      className="ml-2"
                    >
                      <Copy size={16} className={copied['Branch Code'] ? 'text-green-600' : 'text-gray-600'} />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Payment Reference - Highlighted */}
              <div className="p-6 bg-gradient-to-r from-[#6B0B0B]/10 to-[#D4AF37]/10 rounded-xl border-2 border-[#6B0B0B]/30">
                <div className="flex items-center mb-3">
                  <Hash className="text-[#6B0B0B] mr-2" size={24} />
                  <h3 className="text-lg font-bold text-[#6B0B0B]">CRITICAL: Payment Reference</h3>
                </div>
                <p className="text-sm text-gray-700 mb-4">
                  Please use your booking reference as the payment reference. This is essential for us to identify and confirm your payment.
                </p>
                <div className="flex items-center justify-between p-5 bg-white rounded-lg border-2 border-[#6B0B0B]">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Your Payment Reference:</p>
                    <p className="font-mono font-bold text-2xl text-[#6B0B0B]">{bookingDetails.id}</p>
                  </div>
                  <Button
                    onClick={() => copyToClipboard(bookingDetails.id, 'Reference')}
                    className="bg-[#6B0B0B] hover:bg-[#8B1515] text-white"
                  >
                    <Copy size={18} className="mr-2" />
                    {copied['Reference'] ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
              </div>

              {/* Booking Summary */}
              <div className="p-6 bg-gray-50 rounded-xl border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                  <CreditCard className="mr-2 text-[#6B0B0B]" size={20} />
                  Your Booking Summary
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Name:</span>
                    <span className="font-medium text-gray-900">{bookingDetails.firstName} {bookingDetails.surname}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">ID/Passport:</span>
                    <span className="font-medium text-gray-900">{bookingDetails.idOrPassport}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Email:</span>
                    <span className="font-medium text-gray-900">{bookingDetails.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tour:</span>
                    <span className="font-medium text-gray-900">{bookingDetails.tourId}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Date:</span>
                    <span className="font-medium text-gray-900">{bookingDetails.date}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Guests:</span>
                    <span className="font-medium text-gray-900">{bookingDetails.guests}</span>
                  </div>
                  <div className="flex justify-between pt-3 border-t border-gray-300">
                    <span className="text-gray-900 font-semibold">Status:</span>
                    <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold">
                      PENDING PAYMENT
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* What Happens Next */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-xl text-[#6B0B0B]">What Happens Next?</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-3 text-sm text-gray-700">
              <li className="flex items-start">
                <span className="flex-shrink-0 w-6 h-6 bg-[#6B0B0B] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-0.5">1</span>
                <span>Complete the EFT payment using your banking app or online banking</span>
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 w-6 h-6 bg-[#6B0B0B] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-0.5">2</span>
                <span>Use your booking reference <strong>({bookingDetails.id})</strong> as the payment reference</span>
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 w-6 h-6 bg-[#6B0B0B] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-0.5">3</span>
                <span>We will receive and verify your payment (typically within 1-2 business days)</span>
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 w-6 h-6 bg-[#6B0B0B] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-0.5">4</span>
                <span>You will receive an email confirmation once your payment is verified and your booking is confirmed</span>
              </li>
            </ol>
          </CardContent>
        </Card>

        {/* Important Notes */}
        <Card className="mb-8 border-l-4 border-blue-500 bg-blue-50/50">
          <CardContent className="pt-6">
            <h3 className="font-semibold text-blue-900 mb-3">Important Notes:</h3>
            <ul className="space-y-2 text-sm text-blue-800">
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Proof of payment submitted is <strong>not final confirmation</strong>. Wait for our email confirmation.</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Payment verification may take 1-2 business days (longer over weekends/holidays).</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Keep your booking reference safe – you'll need it for any inquiries.</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>If you have any questions, contact us at <a href="mailto:info@capeatiantictours.co.za" className="underline font-semibold">info@capeatiantictours.co.za</a></span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="border-[#6B0B0B] text-[#6B0B0B] hover:bg-[#6B0B0B] hover:text-white"
          >
            <ArrowLeft className="mr-2" size={18} />
            Back to Home
          </Button>
          <Button
            onClick={() => window.print()}
            className="bg-[#6B0B0B] hover:bg-[#8B1515] text-white"
          >
            Print Instructions
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EFTPaymentInstructions;
